import { useCallback, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService, type WorkerAssignment } from '../../../../services/worker'
import {
  WorkerLocationError,
  tryGetCurrentWorkerLocation,
  type WorkerLocationFix,
} from '../../../../services/workerLocationAdapter'
import {
  createMutationId,
  enqueueMutation,
  readDeviceContext,
  type QueuedPunchAction,
} from '../../../../services/offlineWorkerQueue'
import { requestBackgroundSync } from '../../../../services/workerPwa'
import type { BrowserPlatform } from '../../../../hooks/useBrowserPlatform'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'

/**
 * Punch, break, and on-the-way actions for the browser worker client.
 *
 * The browser's only job is to capture coordinates and submit them. Punch
 * eligibility, geofence evaluation, assignment ownership, and offline review
 * are all decided by the backend — including for a queued punch, which is
 * replayed through the existing offline sync contract and filed for supervisor
 * review exactly as the Android client's is.
 */

const isRemote = (assignment: WorkerAssignment) =>
  String(assignment.workMode || assignment.work_mode || '').toLowerCase() === 'remote'

const asCoordinates = (fix: WorkerLocationFix): GeolocationCoordinates => ({
  latitude: fix.latitude,
  longitude: fix.longitude,
  accuracy: fix.accuracyMeters ?? 0,
  altitude: null,
  altitudeAccuracy: null,
  heading: null,
  speed: null,
  toJSON: () => ({}),
} as unknown as GeolocationCoordinates)

export interface WorkerPunchActions {
  locationMessage: string
  submitPunch: (action: QueuedPunchAction, assignment: WorkerAssignment) => Promise<void>
  markOnTheWay: (assignment: WorkerAssignment) => Promise<void>
}

export function useWorkerPunchActions(input: {
  state: WorkerWorkspaceState
  platform: BrowserPlatform
}): WorkerPunchActions {
  const { t } = useI18n()
  const { state, platform } = input
  const [locationMessage, setLocationMessage] = useState('')

  const explainLocationError = useCallback((error: WorkerLocationError) => {
    if (error.reason === 'permission_denied') return t('Location permission is required for an onsite punch. Enable it for this site and try again.')
    if (error.reason === 'unsupported') return t('This browser cannot share your location, so an onsite punch is not possible here.')
    if (error.reason === 'stale') return t('Your browser returned an out-of-date location. Move outdoors or try again in a moment.')
    if (error.reason === 'timeout') return t('The browser did not return a location in time. Try again.')
    return t('Your location could not be determined. Try again once you have a clearer view of the sky.')
  }, [t])

  const submitPunch = useCallback(async (action: QueuedPunchAction, assignment: WorkerAssignment) => {
    setLocationMessage('')
    const needsLocation = !isRemote(assignment) && (action === 'punch-in' || action === 'punch-out')
    let fix: WorkerLocationFix | null = null

    if (needsLocation || platform.features.location.usable) {
      const result = await tryGetCurrentWorkerLocation({ enableHighAccuracy: true })
      fix = result.fix
      if (!fix && needsLocation) {
        setLocationMessage(explainLocationError(result.error!))
        state.setError(explainLocationError(result.error!))
        return
      }
      if (fix) {
        setLocationMessage(t('Location captured to {accuracy} m accuracy.', {
          accuracy: String(Math.round(fix.accuracyMeters ?? 0)),
        }))
      }
    }

    // Offline: record the action locally with its captured coordinates and a
    // generated idempotency key. Nothing is reported as done until the backend
    // acknowledges the replay.
    if (!platform.online) {
      if (!fix) {
        state.setError(t('This action needs your location and cannot be saved offline without it.'))
        return
      }
      if (!platform.features.offline.usable) {
        state.setError(t('This browser cannot store offline actions, so this punch cannot be saved until you reconnect.'))
        return
      }
      await enqueueMutation({
        id: createMutationId(action),
        kind: 'punch',
        action,
        assignmentId: assignment.id,
        locationId: assignment.location_id || null,
        capturedAt: new Date(fix.capturedAt).toISOString(),
        latitude: fix.latitude,
        longitude: fix.longitude,
        accuracyMeters: fix.accuracyMeters,
        deviceContext: readDeviceContext(),
      })
      await platform.refreshQueue()
      void requestBackgroundSync()
      state.setNotice(t('Saved on this device. It will be submitted for review when you reconnect.'))
      return
    }

    await state.run(
      `punch-${assignment.id}`,
      () => workerService.punch(action, assignment.id, assignment.location_id, fix ? asCoordinates(fix) : undefined),
      action === 'punch-in' ? t('Punched in.')
        : action === 'punch-out' ? t('Punched out.')
          : action === 'break-start' ? t('Break started.')
            : t('Break ended.')
    )
  }, [explainLocationError, platform, state, t])

  const markOnTheWay = useCallback(async (assignment: WorkerAssignment) => {
    setLocationMessage('')
    const { fix } = await tryGetCurrentWorkerLocation({ enableHighAccuracy: false })

    if (!platform.online) {
      if (!platform.features.offline.usable) {
        state.setError(t('This browser cannot store offline actions, so this cannot be saved until you reconnect.'))
        return
      }
      await enqueueMutation({
        id: createMutationId('on-the-way'),
        kind: 'on_the_way',
        action: 'on_the_way',
        assignmentId: assignment.id,
        locationId: assignment.location_id || null,
        capturedAt: new Date().toISOString(),
        latitude: fix?.latitude ?? null,
        longitude: fix?.longitude ?? null,
        accuracyMeters: fix?.accuracyMeters ?? null,
        deviceContext: readDeviceContext(),
      })
      await platform.refreshQueue()
      void requestBackgroundSync()
      state.setNotice(t('Saved on this device. It will be sent when you reconnect.'))
      return
    }

    await state.run(
      `on-the-way-${assignment.id}`,
      () => workerService.markOnWay(assignment.id, fix ? asCoordinates(fix) : undefined),
      t('Your organization was told you are on the way.')
    )
  }, [platform, state, t])

  return { locationMessage, submitPunch, markOnTheWay }
}
